import { appConfig } from 'config/environment/appConfig'
import i18next from 'i18next'

import { getSharedKey } from 'utils/helpers/i18n/getKeys'

import { getLocalStorageData, STORAGE_KEY } from './localStorage.service'
import { assertHttpResponse } from './ResponseHandlerService'

enum HTTP_METHOD {
  GET = 'get',
  POST = 'post',
  PATCH = 'patch',
  PUT = 'put',
  DELETE = 'delete',
}

export type RequestOptions = {
  body?: any
  requestFromPromo?: boolean
  headers?: HeadersInit
  disableToken?: boolean
}

export class HttpService {
  private static headers: HeadersInit

  constructor(
    private prefixUrl?: string,
    private newPrefixUrl?: string,
  ) {}

  async get<T>(url: string, options?: RequestOptions): Promise<T> {
    return this.request(HTTP_METHOD.GET, url, options)
  }

  async post<T>(url: string, options?: RequestOptions): Promise<T> {
    return this.request(HTTP_METHOD.POST, url, options)
  }

  async delete<T>(url: string, options?: RequestOptions): Promise<T> {
    return this.request(HTTP_METHOD.DELETE, url, options)
  }

  async put<T>(url: string, options?: RequestOptions): Promise<T> {
    return this.request(HTTP_METHOD.PUT, url, options)
  }

  async patch<T>(url: string, options?: RequestOptions): Promise<T> {
    return this.request(HTTP_METHOD.PATCH, url, options)
  }

  async request<T>(
    method: HTTP_METHOD,
    url: string,
    { body, requestFromPromo, headers = {}, disableToken }: RequestOptions = {},
  ): Promise<T> {
    const token = await this.getTokens(Boolean(requestFromPromo))

    const headersForLK = requestFromPromo
      ? undefined
      : {
          'X-App-Version': 'v20.00.00',
          'X-App-Type': 'prod',
        }

    const request: RequestInit = {
      method,
      headers: {
        Accept: 'application/json, text/plain, */*, promo',
        'Content-Type': 'application/json',
        ...headersForLK,
        ...headers,
      },
    }

    if (!disableToken && token) {
      request.headers = { 'X-Token': token, ...request.headers }
    }

    if (method !== HTTP_METHOD.GET) {
      request.body = JSON.stringify(body)
    }

    if (this.newPrefixUrl) {
      try {
        const fullUrl = this.buildFullUrl(url, this.newPrefixUrl)

        const resp = await fetch(fullUrl, request)
        await assertHttpResponse(resp, fullUrl, request)

        return (await resp.json()) as Promise<T>
      } catch (error) {
        return await this.generalRequest(url, request)
      }
    } else {
      return await this.generalRequest(url, request)
    }
  }

  async generalRequest<T>(url: string, request: RequestInit) {
    let xRequestId: string | null = ''
    const errorCodeLabel = i18next.t(getSharedKey('api.X-Request-Id'))

    try {
      const fullUrl = this.prefixUrl ? this.buildFullUrl(url, this.prefixUrl) : url

      const resp = await fetch(fullUrl, request)

      xRequestId = resp.headers.get('X-Request-Id')

      const retryResp = await assertHttpResponse(resp, fullUrl, request)

      return retryResp ? ((await retryResp.json()) as Promise<T>) : ((await resp.json()) as Promise<T>)
    } catch (error) {
      throw new Error(`${(error as Error).message}${xRequestId ? `\n\n${errorCodeLabel}: ${xRequestId}` : ''}`)
    }
  }

  setPrefixUrl(prefix: string) {
    this.prefixUrl = prefix
    return this
  }

  async getTokens(requestFromPromo: boolean) {
    const token = getLocalStorageData(requestFromPromo ? STORAGE_KEY.USER_TOKEN : STORAGE_KEY.ACCESS_TOKEN)
    return token ? token : getLocalStorageData(STORAGE_KEY.ACCESS_TOKEN) || getLocalStorageData(STORAGE_KEY.USER_TOKEN)
  }

  buildFullUrl(url: string, apiUrl = appConfig.API_URL) {
    if (url.includes(apiUrl)) return url

    url = url.startsWith('/') ? url : `/${url}`
    const prefix = apiUrl[apiUrl.length - 1] === '/' ? apiUrl.slice(0, -1) : apiUrl
    return `${prefix}${url}`
  }

  static setHeaders(headers: HeadersInit) {
    this.headers = { ...this.headers, ...headers }
    return this
  }
}
